<template>
  <KeepAlive>
    <ChatList
      v-if="isListRoute"
      ref="chatList"
      :initial-data="initialData"
      :store="store"
      :highlighted-public-customer-id="highlightedPublicCustomerId"
      @open-chat="openChat"
      @notify="notification.show"
    />
  </KeepAlive>

  <ChatDetail
    v-if="isDetailRoute"
    ref="chatDetail"
    :key="publicCustomerId"
    :public-customer-id="publicCustomerId"
    :oshi-detail-url="urlFromTemplate(initialData.oshi_detail_url_template, publicCustomerId)"
    :call-slot-list-url="initialData.call_slot_list_url_template ? urlFromTemplate(initialData.call_slot_list_url_template, publicCustomerId) : ''"
    :state="detailState"
    :labels="initialData.labels"
    :messages-api-url="urlFromTemplate(initialData.chat_messages_api_url_template, publicCustomerId)"
    :message-updates-api-url="initialData.chat_message_updates_api_url_template ? urlFromTemplate(initialData.chat_message_updates_api_url_template, publicCustomerId) : ''"
    :polling-disabled="store.webPushConfirmed"
    :read-api-url="urlFromTemplate(initialData.chat_read_api_url_template, publicCustomerId)"
    :read-disabled="store.readDisabled"
    :moderation-api-url="initialData.chat_moderation_api_url_template ? urlFromTemplate(initialData.chat_moderation_api_url_template, publicCustomerId) : ''"
    :preferences-csrf-token="initialData.chat_preferences_csrf_token"
    :read-csrf-token="initialData.read_csrf_token"
    :send-api-url="urlFromTemplate(initialData.chat_send_api_url_template, publicCustomerId)"
    :send-csrf-token="initialData.send_csrf_token"
    :image-send-api-url="urlFromTemplate(initialData.chat_image_send_api_url_template, publicCustomerId)"
    :image-send-csrf-token="initialData.image_send_csrf_token"
    :image-max-file-size="initialData.image_max_file_size"
    :image-token-quantity="initialData.image_token_quantity"
    :reaction-api-url-template="initialData.chat_reaction_api_url_template"
    :reaction-csrf-token="initialData.reaction_csrf_token"
    :reaction-max-count="initialData.reaction_max_count"
    :reaction-save-delay="initialData.reaction_save_delay"
    :reaction-presets="initialData.reaction_presets"
    :upload-list-api-url="initialData.chat_upload_list_api_url"
    :upload-delete-api-url="initialData.chat_upload_delete_api_url"
    :upload-delete-csrf-token="initialData.upload_delete_csrf_token"
    :upload-delete-limit="initialData.upload_delete_limit"
    :favorite-api-url="urlFromTemplate(initialData.chat_favorite_api_url_template, publicCustomerId)"
    :favorite-csrf-token="initialData.favorite_csrf_token"
    :block-api-url="urlFromTemplate(initialData.chat_block_api_url_template, publicCustomerId)"
    :block-csrf-token="initialData.block_csrf_token"
    :report-api-url="urlFromTemplate(initialData.chat_report_api_url_template, publicCustomerId)"
    :report-csrf-token="initialData.report_csrf_token"
    :report-reasons="initialData.report_reasons"
    :report-body-max-length="initialData.report_body_max_length"
    :display-name-api-url="urlFromTemplate(initialData.chat_display_name_api_url_template, publicCustomerId)"
    :display-name-csrf-token="initialData.display_name_csrf_token"
    :display-name-max-length="initialData.display_name_max_length"
    :theme-api-url="urlFromTemplate(initialData.chat_theme_api_url_template, publicCustomerId)"
    :theme-csrf-token="initialData.theme_csrf_token"
    :is-oshi-customer="initialData.is_oshi_customer"
    :token-unit-length="initialData.token_unit_length"
    :token-balance-store="tokenBalanceStore"
    @back="backToList"
    @read-success="markListRead"
    @unavailable="handleUnavailable"
    @notify="notification.show"
    @send-success="handleSendSuccess"
    @favorite-success="handleFavoriteSuccess"
    @display-name-success="handleDisplayNameSuccess"
    @error="handleError"
  />

  <ChatErrorModal
    :message="errorState.message"
    :fatal="errorState.fatal"
    :product-list="errorState.productList"
    :labels="initialData.labels"
    @close="closeError"
    @chat-list="openChatList"
    @product-list="openProductList"
  />
  <AppNotification
    :notification="notification.current.value"
    :visible="notification.visible.value"
    @close="notification.close"
    @after-leave="notification.afterLeave"
    @activate="activateNotification"
  />
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import ChatDetail from './ChatDetail.vue';
import ChatErrorModal from './ChatErrorModal.vue';
import ChatList from './ChatList.vue';
import AppNotification from './common/AppNotification.vue';
import {internalNotificationPath, useAppNotification} from '../composables/useAppNotification.js';
import {createChatStore, getChatDetailState} from '../stores/chatStore.js';
import {consumeTokenBalances, tokenBalanceStore} from '../stores/tokenBalanceStore.js';

const props = defineProps({initialData: {type: Object, required: true}});
const route = useRoute();
const router = useRouter();
const store = createChatStore();
store.readDisabled = !!props.initialData.chat_read_disabled_flg;
const chatList = ref(null);
const chatDetail = ref(null);
const highlightedPublicCustomerId = ref(null);
const errorState = ref({message: null, fatal: false, productList: false});
const notification = useAppNotification();
const isListRoute = computed(() => route.name === 'chat-list');
const isDetailRoute = computed(() => route.name === 'chat-detail');
const publicCustomerId = computed(() => String(route.params.public_customer_id ?? props.initialData.public_customer_id ?? ''));
const detailState = computed(() => getChatDetailState(store, publicCustomerId.value));
store.list.updateCursor ??= String(props.initialData.chat_update_cursor ?? '0');
const urlFromTemplate = (template, id) => template.replace('%public_customer_id%', encodeURIComponent(String(id)));

const openChat = (id) => router.push(urlFromTemplate(props.initialData.chat_detail_url_template, id));
const backToList = async () => {
  highlightedPublicCustomerId.value = publicCustomerId.value;
  await router.push(props.initialData.chat_list_url);
  await nextTick();
  chatList.value?.restoreListPosition();
};
const handleUnavailable = async (message) => {
  await router.replace(props.initialData.chat_list_url);
  errorState.value = {message, fatal: false, productList: false};
};
const handleError = ({message, fatal = false, productList = false}) => {
  errorState.value = {message, fatal, productList};
};
const closeError = () => {
  errorState.value = {message: null, fatal: false, productList: false};
};
const openChatList = () => window.location.assign(props.initialData.chat_list_url);
const openProductList = () => window.location.assign(props.initialData.product_list_url);
const markListRead = (id) => {
  Object.entries(store.list.categoryStates).forEach(([key, state]) => {
    if (!state.isLoaded || store.readDisabled) {
      return;
    }
    if (key === 'system-2') {
      state.chats = state.chats.filter((chat) => String(chat.public_customer_id) !== String(id));
      return;
    }
    state.chats.forEach((chat) => {
      if (String(chat.public_customer_id) === String(id)) {
        chat.unread_count = 0;
      }
    });
  });
};
const handleSendSuccess = (id, message, consumedBalances, tokenTotalQuantity) => {
  Object.entries(store.list.categoryStates).forEach(([key, state]) => {
    if (!state.isLoaded || message.pending_moderation) {
      return;
    }
    const index = state.chats.findIndex((chat) => String(chat.public_customer_id) === String(id));
    if (index < 0) {
      return;
    }
    if (key === 'system-2' && !store.readDisabled) {
      state.chats.splice(index, 1);
      return;
    }
    const chat = state.chats.splice(index, 1)[0];
    chat.latest_message = message.type === 'image' ? props.initialData.labels.image_sent : message.body;
    chat.latest_message_time_label = props.initialData.labels.recent;
    if (!store.readDisabled) chat.unread_count = 0;
    state.chats.unshift(chat);
  });
  if (!props.initialData.is_oshi_customer && tokenTotalQuantity !== null) {
    consumeTokenBalances(consumedBalances, tokenTotalQuantity);
  }
};
const handleFavoriteSuccess = (id, enabled) => {
  const favoriteState = store.list.categoryStates['system-3'];
  if (!favoriteState?.isLoaded) {
    return;
  }

  const favoriteIndex = favoriteState.chats.findIndex((chat) => String(chat.public_customer_id) === String(id));
  if (!enabled) {
    if (favoriteIndex >= 0) {
      favoriteState.chats.splice(favoriteIndex, 1);
    }
    return;
  }
  if (favoriteIndex >= 0) {
    return;
  }

  const source = Object.entries(store.list.categoryStates)
    .filter(([key, state]) => key !== 'system-3' && state.isLoaded)
    .flatMap(([, state]) => state.chats)
    .find((chat) => String(chat.public_customer_id) === String(id));
  if (source !== undefined) {
    favoriteState.chats.unshift({...source});
    return;
  }

  favoriteState.chats = [];
  favoriteState.nextCursor = null;
  favoriteState.hasNext = false;
  favoriteState.isLoaded = false;
};
const handleDisplayNameSuccess = (id, nickname) => {
  Object.values(store.list.categoryStates).forEach((state) => {
    if (!state.isLoaded) {
      return;
    }
    state.chats.forEach((chat) => {
      if (String(chat.public_customer_id) === String(id)) {
        chat.nickname = nickname;
      }
    });
  });
};
const decimalGreaterThan = (left, right) => {
  const a = String(left).replace(/^0+(?=\d)/, '');
  const b = String(right).replace(/^0+(?=\d)/, '');
  return a.length !== b.length ? a.length > b.length : a.localeCompare(b) > 0;
};
const pushedPublicCustomerId = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const [prefix, suffix = ''] = props.initialData.chat_detail_url_template.split('%public_customer_id%');
    if (!path.startsWith(prefix) || !path.endsWith(suffix)) return null;
    return decodeURIComponent(path.slice(prefix.length, suffix === '' ? undefined : -suffix.length));
  } catch (error) {
    return null;
  }
};
const requestChatUpdates = (id, cursor) => $.ajax({
  url: urlFromTemplate(props.initialData.chat_message_updates_api_url_template, id),
  method: 'POST',
  contentType: 'application/json; charset=UTF-8',
  dataType: 'json',
  data: JSON.stringify({cursor}),
});
const pendingPushMessageIds = new Map();
const pushUpdateRequests = new Map();
let notificationAudioContext = null;
const unlockNotificationSound = async () => {
  const AudioContext = window.AudioContext ?? window.webkitAudioContext;
  if (typeof AudioContext !== 'function') return null;
  notificationAudioContext ??= new AudioContext();
  if (notificationAudioContext.state === 'suspended') await notificationAudioContext.resume();
  return notificationAudioContext.state === 'running' ? notificationAudioContext : null;
};
const playNotificationSound = async () => {
  try {
    const context = await unlockNotificationSound();
    if (context === null) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.12, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.16);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + 0.16);
  } catch (error) {
    // ブラウザが自動再生を許可しない場合は画面更新だけを継続する。
  }
};
const applyChatSummary = async (chat) => {
  if (chat === null) return;
  if (chatList.value?.applyChatUpdate) {
    await chatList.value.applyChatUpdate(chat);
    return;
  }
  Object.values(store.list.categoryStates).forEach((state) => { if (state.isLoaded) state.isLoaded = false; });
};
const handlePushMessage = async (event) => {
  const payload = event.data;
  if (payload?.type !== 'chat-message' || typeof payload.data?.chat_message_id !== 'string') return;
  if (payload.data.play_sound === true) playNotificationSound();
  const id = pushedPublicCustomerId(payload.data.url);
  if (id === null) return;
  const state = getChatDetailState(store, id);
  state.updateCursor ??= String(store.list.updateCursor ?? '0');
  const currentTarget = pendingPushMessageIds.get(id) ?? state.updateCursor;
  if (decimalGreaterThan(payload.data.chat_message_id, currentTarget)) {
    pendingPushMessageIds.set(id, payload.data.chat_message_id);
  }
  if (!decimalGreaterThan(pendingPushMessageIds.get(id) ?? state.updateCursor, state.updateCursor)) return;
  if (pushUpdateRequests.has(id)) return pushUpdateRequests.get(id);

  const updateRequest = (async () => {
    try {
      let chat = null;
      while (true) {
        while (decimalGreaterThan(pendingPushMessageIds.get(id) ?? state.updateCursor, state.updateCursor)) {
          const previousCursor = state.updateCursor;
          if (id === publicCustomerId.value && chatDetail.value?.fetchIncoming) {
            chat = await chatDetail.value.fetchIncoming(false) ?? chat;
          } else {
            let hasNext = true;
            while (hasNext) {
              const response = await requestChatUpdates(id, state.updateCursor);
              if (response?.status !== 'OK' || response.result === null || !Array.isArray(response.result.messages)) return;
              if (state.isLoaded) {
                const known = new Set(state.messages.map((message) => String(message.id)));
                const additions = response.result.messages.filter((message) => !known.has(String(message.id)));
                if (additions.length > 0) {
                  state.messages.push(...additions);
                  state.hasUnread = true;
                  state.readAttempted = false;
                }
              }
              state.updateCursor = String(response.result.next_cursor);
              chat = response.result.chat ?? chat;
              hasNext = response.result.has_next === true;
            }
          }
          if (state.updateCursor === previousCursor) return;
        }
        await applyChatSummary(chat);
        if (!decimalGreaterThan(pendingPushMessageIds.get(id) ?? state.updateCursor, state.updateCursor)) break;
      }
      store.webPushConfirmed = true;
      if (id === publicCustomerId.value) chatDetail.value?.stopUpdatePolling?.();
      chatList.value?.stopUpdatePolling?.();
    } catch (error) {
      // Push取得失敗時は30秒ポーリングを継続する。
    } finally {
      pendingPushMessageIds.delete(id);
      pushUpdateRequests.delete(id);
    }
  })();
  pushUpdateRequests.set(id, updateRequest);
  return updateRequest;
};
const activateNotification = async (item) => {
  const path = internalNotificationPath(item.url);
  if (path === null) {
    return;
  }
  notification.close();
  if (item.spa) {
    await router.push(path);
  } else {
    window.location.assign(path);
  }
};

const removeNavigationGuard = router.beforeEach(async (to, from) => {
  if (from.name !== 'chat-detail' || to.fullPath === from.fullPath) {
    return true;
  }
  if (typeof chatDetail.value?.flushPendingReactions !== 'function') {
    return true;
  }

  return await chatDetail.value.flushPendingReactions() !== false;
});

const serviceWorker = navigator.serviceWorker;
const unlockSoundOnInteraction = () => { unlockNotificationSound(); };
const reportChatPageState = (worker = serviceWorker?.controller) => {
  worker?.postMessage({type: 'chat-page-state', url: window.location.href});
};
const handleControllerChange = () => { reportChatPageState(); };
onBeforeUnmount(removeNavigationGuard);
onMounted(() => {
  serviceWorker?.addEventListener('message', handlePushMessage);
  serviceWorker?.addEventListener('controllerchange', handleControllerChange);
  window.addEventListener('pointerdown', unlockSoundOnInteraction, {once: true});
  window.addEventListener('keydown', unlockSoundOnInteraction, {once: true});
  serviceWorker?.register?.('/service-worker.js').then((registration) => {
    reportChatPageState(serviceWorker.controller ?? registration.active);
  }).catch(() => {
    // 登録・更新できない場合は30秒ポーリングを継続する。
  });
});
onBeforeUnmount(() => {
  serviceWorker?.removeEventListener('message', handlePushMessage);
  serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
  window.removeEventListener('pointerdown', unlockSoundOnInteraction);
  window.removeEventListener('keydown', unlockSoundOnInteraction);
});

watch(() => route.fullPath, () => {
  document.body.classList.toggle('chat-list-page', route.name === 'chat-list');
  document.body.classList.toggle('chat-detail-page', route.name === 'chat-detail');
  reportChatPageState();
}, {immediate: true});
</script>
